import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../../../redux/baseQuery';
import { getLocation } from '../../../graphql/postgress_graphql/queries';

// Types for Pincode Location API
export interface PincodeLocationResponse {
  District: string;
  State: string;
  PinCode: string;
  City: string;
  Status: string;
}

export interface PincodeLocationRequest {
  pincode: string;
}

// Cache management for pincode queries
const MAX_CACHED_PINCODES = 5;
const pincodeCache = new Map<string, number>(); // pincode -> timestamp

export const pincodeLocationApi = createApi({
  reducerPath: 'pincodeLocation',
  baseQuery: baseQuery,
  tagTypes: ['PincodeLocation'],
  endpoints: (builder) => ({
    fetchPincodeLocation: builder.query<PincodeLocationResponse, PincodeLocationRequest>({
      query: ({ pincode }) => ({
        body: getLocation,
        variables: { pincode },
      }),
      keepUnusedDataFor: Infinity, // Cache for infinite time
      providesTags: (result, error, { pincode }) => [{ type: 'PincodeLocation', id: pincode }],
      transformResponse: (response) => {
        if (response?.length) {
          return {
            District: response[0].district.toLowerCase().replace(/\b\w/g, (match: String) => match.toUpperCase()),
            State: response[0].state,
            PinCode: response[0].pincode,
            City: response[0].city,
            Status: 'success',
          };
        } else {
          throw new Error('No location data found');
        }
      },
      // Custom cache management to keep only 5 pincodes
      onQueryStarted: async (arg, { dispatch, queryFulfilled, getState }) => {
        try {
          await queryFulfilled;

          const { pincode } = arg;
          const currentTime = Date.now();

          // Add current pincode to cache with timestamp
          pincodeCache.set(pincode, currentTime);

          // Check if we have more than 5 pincodes cached
          if (pincodeCache.size > MAX_CACHED_PINCODES) {
            // Sort pincodes by timestamp (oldest first)
            const sortedPincodes = Array.from(pincodeCache.entries()).sort(([, a], [, b]) => a - b);

            // Remove the oldest pincodes to keep only 5
            const pincodesToRemove = sortedPincodes.slice(0, pincodeCache.size - MAX_CACHED_PINCODES);

            pincodesToRemove.forEach(([pincodeToRemove]) => {
              // Remove from our cache
              pincodeCache.delete(pincodeToRemove);

              dispatch(pincodeLocationApi.util.invalidateTags([{ type: 'PincodeLocation', id: pincodeToRemove }]));
            });
          }
        } catch (error) {}
      },
    }),
  }),
  // Custom cache management
  keepUnusedDataFor: Infinity,
});

// Export hooks for usage in functional components
export const { useFetchPincodeLocationQuery, useLazyFetchPincodeLocationQuery } = pincodeLocationApi;

// Export the API for store configuration
export default pincodeLocationApi;
